import OpenInNew from "@mui/icons-material/OpenInNew";
import { Box, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { getStatusLabel } from "encamp-shared/src/submissions";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { MouseEvent, useMemo } from "react";
import { Link } from "react-router-dom";

import { formatDay } from "./manifestCorrectionDisplay";

export type LinkableSubmissionOption = {
  id: string;
  title: string;
  submissionType: string;
  status: string;
  dueDate?: string | null;
  createdAt: string;
  facilityName?: string | null;
  assigneeName?: string | null;
};

type Props = {
  rows: LinkableSubmissionOption[];
  loading: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  /** True when a search is narrowing the list — drives the empty-state copy. */
  searching: boolean;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  /** Builds the per-row submission link-outs. */
  tenantId: string;
};

/**
 * Candidate-submissions grid for "mark resolved → link to an existing
 * submission". Presentational: the parent owns the query and the selection, so
 * a row filtered out by a search can't stay silently submittable.
 */
export function SubmissionPicker({
  rows,
  loading,
  search,
  onSearchChange,
  searching,
  selectedId,
  onSelect,
  tenantId,
}: Props) {
  const columns = useMemo<CustomGridColDef<LinkableSubmissionOption>[]>(
    () => [
      { field: "title", headerName: "Title", flex: 1, minWidth: 180 },
      {
        field: "submissionType",
        headerName: "Type",
        flex: 0.6,
        valueFormatter: (value) => (value != null ? prettyPrintEnumValue(String(value)) : ""),
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.6,
        valueFormatter: (value) => (value != null ? getStatusLabel(String(value)) : ""),
      },
      {
        field: "createdAt",
        headerName: "Created",
        flex: 0.5,
        valueFormatter: (value) => formatDay(value == null ? null : String(value)),
      },
      {
        field: "assigneeName",
        headerName: "Assignee",
        flex: 0.6,
        valueGetter: (_, row) => row.assigneeName ?? "Unassigned",
      },
      {
        field: "actions",
        headerName: "",
        width: 56,
        sortable: false,
        renderCell: (params) => (
          <Tooltip title="Open submission in a new tab">
            {/* One interactive element (a button that IS the link) — a button nested
                inside an anchor is invalid and double-stops keyboard focus. */}
            <IconButton
              size="small"
              component={Link}
              to={`/o/${tenantId}/waste/submissions/${params.row.id}/edit`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open submission in a new tab"
              onClick={(e: MouseEvent) => e.stopPropagation()}
            >
              <OpenInNew fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [tenantId]
  );

  return (
    <Box sx={{ mt: 1 }}>
      <TextField
        label="Search submissions by title"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        size="small"
        fullWidth
        sx={{ mb: 1 }}
      />
      <DataGrid<LinkableSubmissionOption>
        rows={rows}
        columns={columns}
        loading={loading}
        noRowsMessage={
          searching
            ? "No submissions match that title."
            : "No recent submissions for this facility."
        }
        // Visible single-select: a checkbox per row (the select-all header
        // checkbox is hidden below) so the current pick is unmissable —
        // row-highlight-only selection was easy to miss and cleared silently.
        checkboxSelection
        disableMultipleRowSelection
        rowSelectionModel={{
          type: "include",
          ids: new Set(selectedId ? [selectedId] : []),
        }}
        onRowSelectionModelChange={(model) => {
          const [id] = model.type === "include" ? Array.from(model.ids) : [];
          onSelect(id == null ? null : String(id));
        }}
        pagination
        paginationMode="client"
        pageSizeOptions={[5, 10, 25]}
        initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
        sx={{
          maxHeight: 360,
          // Single-select: hide the header's select-all checkbox (same
          // treatment as OmnisearchDataGrid's disableSelectAll).
          "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer": {
            display: "none",
          },
        }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
        Check the submission to link; use the open-in-new-tab icon to verify it first.
      </Typography>
    </Box>
  );
}
